// src/entities/authorComposer/components/common/AuthorCreatedOffer.tsx
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { restFetch } from "../../../../api/apiClient";
import { useRootStore } from "../../../../store/contexts";

export interface AuthorCreatedOfferProps {
  entity: string;
  recordId: string;
  values: Record<string, any>;
  onDone: () => void;
}

/**
 * `fallida` es cuando se sabe que la solicitud no salió: la app contestó con su
 * error (el `error` del JSON de la respuesta), y la ruta contesta sus errores
 * sin haber encolado el mail; la sesión estaba vencida (antes de enviar, o la
 * app contestó 401 y no se pudo renovar); o el sitio está en mantenimiento.
 * `sinRespuesta` es cuando no llegó una respuesta de la app que se pueda leer:
 * plazo vencido, red caída, una respuesta que no es de la app (cualquier estado
 * que no es 2xx sin el JSON de la app, ver RESPUESTA_QUE_NO_ES_DE_LA_APP), un
 * 2xx con un cuerpo que no es JSON y, como defensa, un 2xx sin `success`, que la
 * ruta no da (sus 2xx traen siempre `success: true`). El server graba el envío y
 * encola el mail antes de contestar, y no se detiene si el cliente corta, así
 * que pudo haber salido. Decir «no se pudo» llevaría a reenviarla, y al autor le
 * llegaría un segundo mail.
 */
type Estado =
  | "pregunta"
  | "enviando"
  | "enviada"
  | "yaValidado"
  | "yaVerificado"
  | "demorada"
  | "fallida"
  | "sinRespuesta";

/**
 * Cuánto se espera el envío. Mientras se envía, el diálogo no se puede cerrar
 * (el resultado tiene que quedar a la vista): un pedido colgado lo trabaría.
 * Vencido, se corta el pedido y se dice que no llegó la respuesta.
 */
const PLAZO_DEL_ENVIO_MS = 30_000;

/**
 * El mensaje que arma restFetch para un estado que no es 2xx cuando el cuerpo
 * no trae el `error` de la app: «HTTP 502: Bad Gateway», o «HTTP 502: » sin
 * texto de estado (HTTP/2). Toda respuesta de error de la ruta trae su `error`
 * en un JSON, así que esta no es de la app, sea cual sea el estado: la de un
 * proxy cuando la app no termina de contestar (502, 504), pero también un 503
 * sin el JSON de mantenimiento, o un 404 o un 413 del proxy. En algunos de esos
 * el pedido no llegó a la app; igual van todos a `sinRespuesta`, que es la
 * dirección segura: dice «puede que se haya enviado», y nunca «no se pudo»
 * cuando la solicitud pudo haber salido.
 */
const RESPUESTA_QUE_NO_ES_DE_LA_APP = /^HTTP \d{3}:/;

/**
 * El resultado de un envío que lanzó. restFetch lanza un Error con el `error`
 * del cuerpo cuando la app contesta con un estado que no es 2xx (la demora es
 * un 429 con «rate_limited», emailVerification.routes.ts): la app contestó.
 * Va a `sinRespuesta` lo que no deja afirmar que no salió: fetch lanza un
 * TypeError (falla la red, también después de que el pedido llegó), la
 * respuesta no es de la app (RESPUESTA_QUE_NO_ES_DE_LA_APP), o un 2xx trae un
 * cuerpo que no es JSON (response.json() lanza un SyntaxError: la app pudo
 * haberlo procesado).
 */
function estadoDelError(error: unknown): Estado {
  if (error instanceof TypeError || error instanceof SyntaxError) return "sinRespuesta";
  if (error instanceof Error && RESPUESTA_QUE_NO_ES_DE_LA_APP.test(error.message)) return "sinRespuesta";
  return error instanceof Error && error.message === "rate_limited" ? "demorada" : "fallida";
}

/** Un email o un nombre largo, sin espacios, se corta dentro del diálogo en vez de desbordarlo. */
const CORTAR_LO_LARGO = { overflowWrap: "anywhere" } as const;

/**
 * Recién guardada el alta, la oferta de la solicitud de validación, solo si el
 * usuario puede enviarla: el server exige authorComposer_update para
 * /api/email-verification/send. Un rol que crea autores pero no los actualiza
 * recibiría un rechazo y un texto que para él no es cierto («Puede enviarla
 * desde el formulario del autor»). Sin ese permiso no se ofrece, y el alta
 * termina enseguida (onDone).
 */
export const AuthorCreatedOffer: React.FC<AuthorCreatedOfferProps> = observer((props) => {
  const { authStore } = useRootStore();
  if (!authStore.hasPermissions("authorComposer_update")) return <SinOferta onDone={props.onDone} />;
  return <OfertaDeValidacion {...props} />;
});

/** Sin oferta: dice una sola vez que el alta terminó, aunque se vuelva a dibujar. */
const SinOferta: React.FC<{ onDone: () => void }> = ({ onDone }) => {
  const terminada = useRef(false);
  useEffect(() => {
    if (terminada.current) return;
    terminada.current = true;
    onDone();
  }, [onDone]);
  return null;
};

/**
 * Ofrece mandarle al autor la solicitud de validación: con el alta mínima, es
 * el paso siguiente. El ícono «Validar email y datos», junto al Email, sigue en
 * la vista y en la edición para hacerlo después.
 *
 * El pedido lleva `scope: "data"`: validar el email y los datos de este autor,
 * no solo verificar el email. Un email ya verificado por otro registro con la
 * misma dirección (el contacto de una editorial) no frena la validación: la
 * solicitud sale igual. No se manda `resend`: si los datos de este autor ya
 * estuvieran confirmados, el server lo dice (`already_validated`) y no se
 * pide otra validación sin que nadie lo decida.
 *
 * `already_verified` es la respuesta de un server anterior a `scope`, cuando el
 * email ya estaba verificado, quizás por otro registro: no dice que el autor
 * confirmó sus datos, y se dice así.
 */
const OfertaDeValidacion: React.FC<AuthorCreatedOfferProps> = observer(({ recordId, values, onDone }) => {
  const { langStore } = useRootStore();
  const [estado, setEstado] = useState<Estado>("pregunta");
  const email = String(values.email ?? "").trim();
  const nombre = [values.firstName, values.lastName].filter(Boolean).join(" ").trim();
  // Un solo reemplazo: un nombre con "{email}" se muestra tal cual.
  const completar = (plantilla: string) =>
    plantilla.replace(/\{(name|email)\}/g, (_, clave: string) => (clave === "name" ? nombre : email));

  const enviar = async () => {
    setEstado("enviando");
    const corte = new AbortController();
    // Vencido el plazo, se dice enseguida que no llegó la respuesta, aunque el pedido no atienda el
    // corte (la espera del token, por ejemplo); si después contesta igual, se descarta.
    const plazo = setTimeout(() => {
      corte.abort();
      setEstado("sinRespuesta");
    }, PLAZO_DEL_ENVIO_MS);
    try {
      const data = await restFetch("/api/email-verification/send", {
        method: "POST",
        body: JSON.stringify({ email, sourceEntity: "AuthorComposer", sourceRecordId: recordId, scope: "data" }),
        signal: corte.signal,
      });
      if (corte.signal.aborted) return;
      if (!data?.success) setEstado("sinRespuesta");
      else if (data.status === "already_validated") setEstado("yaValidado");
      else if (data.status === "already_verified") setEstado("yaVerificado");
      else setEstado("enviada");
    } catch (error) {
      if (corte.signal.aborted) return;
      setEstado(estadoDelError(error));
    } finally {
      clearTimeout(plazo);
    }
  };

  const preguntando = estado === "pregunta" || estado === "enviando";
  return (
    <Dialog open onClose={estado === "enviando" ? undefined : onDone} maxWidth="xs" fullWidth>
      <DialogTitle>{langStore.authorValidationOfferTitle}</DialogTitle>
      <DialogContent>
        {preguntando && (
          <Typography variant="body2" sx={CORTAR_LO_LARGO}>
            {completar(langStore.authorValidationOfferBody)}
          </Typography>
        )}
        {estado === "enviada" && (
          <Alert severity="success" sx={CORTAR_LO_LARGO}>
            {completar(langStore.authorValidationOfferSent)}
          </Alert>
        )}
        {estado === "yaValidado" && (
          <Alert severity="info" sx={CORTAR_LO_LARGO}>
            {langStore.authorValidationOfferAlreadyValidated}
          </Alert>
        )}
        {estado === "yaVerificado" && (
          <Alert severity="info" sx={CORTAR_LO_LARGO}>
            {langStore.authorValidationOfferAlreadyVerified}
          </Alert>
        )}
        {estado === "demorada" && (
          <Alert severity="info" sx={CORTAR_LO_LARGO}>
            {langStore.authorValidationOfferRateLimited}
          </Alert>
        )}
        {estado === "fallida" && (
          <Alert severity="error" sx={CORTAR_LO_LARGO}>
            {langStore.authorValidationOfferFailed}
          </Alert>
        )}
        {estado === "sinRespuesta" && (
          <Alert severity="warning" sx={CORTAR_LO_LARGO}>
            {langStore.authorValidationOfferNoResponse}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        {preguntando ? (
          <>
            <Button onClick={onDone} disabled={estado === "enviando"}>
              {langStore.authorValidationOfferLater}
            </Button>
            <Button variant="contained" onClick={enviar} disabled={estado === "enviando"}>
              {estado === "enviando" ? <CircularProgress size={20} color="inherit" /> : langStore.authorValidationOfferSend}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={onDone}>
            {langStore.closeCommand}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});
